import React, { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';
import Button from '@shared/ui/Button';

export type ExportFormatId = 'csv' | 'excel' | 'pdf' | 'word';

export interface ExportFormat {
  format: ExportFormatId;
  /** A qualifier shown after the name — "Landscape", "Portrait". */
  note?: string;
  disabled?: boolean;
}

export interface ExportMenuProps {
  /**
   * The formats offered, in the order given. Pass bare ids or objects with a
   * note. The list may change between renders (e.g. per view mode).
   */
  formats: Array<ExportFormatId | ExportFormat>;
  /**
   * Called with the chosen format. If it returns a promise the trigger shows a
   * busy state until it settles — most exports here hit an endpoint and then
   * download, which takes long enough to need feedback.
   */
  onExport: (format: ExportFormatId) => void | Promise<void>;
  /** Trigger label. "Export" unless a surface genuinely needs otherwise. */
  label?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md';
  disabled?: boolean;
  className?: string;
}

const FORMAT_NAMES: Record<ExportFormatId, string> = {
  csv: 'CSV',
  excel: 'Excel',
  pdf: 'PDF',
  word: 'Word',
};

/**
 * The Export control, in one place.
 *
 * Fourteen files each hand-rolled this — a trigger, an absolute-positioned
 * dropdown, a click-outside effect, and a hand-written item per format. Across
 * them the same action shipped under five wordings: "Export PDF",
 * "Export as PDF", "Export to PDF", "Download PDF", "Download as PDF".
 *
 * Menu items name the format alone — "PDF", not "Export PDF". The trigger
 * already says Export; repeating the verb inside its own menu is noise, and it
 * is why five phrasings of the identical action could coexist unnoticed.
 *
 * The menu is a real menu to a keyboard and screen reader: the trigger carries
 * aria-haspopup/aria-expanded, items are menuitems, Escape closes and returns
 * focus to the trigger. None of the fourteen copies had any of that.
 */
const ExportMenu: React.FC<ExportMenuProps> = ({
  formats,
  onExport,
  label = 'Export',
  variant = 'secondary',
  size = 'sm',
  disabled = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const items: ExportFormat[] = formats.map((f) => (typeof f === 'string' ? { format: f } : f));

  const choose = async (format: ExportFormatId) => {
    setOpen(false);
    const result = onExport(format);
    if (result && typeof (result as Promise<void>).then === 'function') {
      setBusy(true);
      try {
        await result;
      } finally {
        setBusy(false);
      }
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <Button
        ref={triggerRef}
        variant={variant}
        size={size}
        icon={Download}
        onClick={() => setOpen((o) => !o)}
        loading={busy}
        disabled={disabled || busy}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label}
      </Button>

      {open && (
        <div
          role="menu"
          aria-label={label}
          className="absolute right-0 z-50 mt-2 w-48 rounded-lg border border-border-light bg-white py-1 shadow-lg dark:border-border-dark dark:bg-background-dark-secondary"
        >
          {items.map(({ format, note, disabled: itemDisabled }) => (
            <button
              key={format + (note ?? '')}
              role="menuitem"
              type="button"
              disabled={itemDisabled}
              onClick={() => choose(format)}
              className="block w-full px-4 py-2 text-left text-sm text-text-primary hover:bg-background-tertiary disabled:opacity-50 dark:text-text-dark-primary dark:hover:bg-background-dark-tertiary"
            >
              {FORMAT_NAMES[format]}
              {note && (
                <span className="ml-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                  {note}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
